import { Navigate } from 'react-router-dom'
import { useAuth } from '../lib/auth'

/**
 * @typedef {'owner' | 'operator' | 'subcontractor' | 'client'} UserRole
 * @typedef {'today' | 'clients-work' | 'money' | 'crew' | 'settings'} NavGroup
 *
 * @typedef {Object} NavLink
 * @property {string} to                        - React Router path, e.g. '/dashboard'
 * @property {string} icon                      - Custom icon name (see src/assets/icons/svgs.js)
 * @property {string} label                     - Display label (English fallback)
 * @property {string} [labelKey]                - Optional terminology key for per-tenant override
 * @property {UserRole[]} roles                 - Roles that see this link in the sidebar
 * @property {boolean} mobileNav                - Include in bottom tab bar on mobile
 * @property {string} [gate]                    - Plan-gate key (planGate.jsx GATE_CONFIG)
 * @property {string} [pageKey]                 - Capability key (pageAccess.js)
 * @property {NavGroup} group                   - Sidebar section this link belongs to
 *
 * @typedef {Object} NavGroupDef
 * @property {NavGroup} key
 * @property {string} label                     - Group header label (uppercase in UI)
 *
 * @typedef {Object} RoleGuardProps
 * @property {UserRole[]} [allowed]             - Required when `pageKey` is not provided
 * @property {string} [pageKey]                 - Checked via `canAccessPage()` — takes precedence over `allowed`
 * @property {import('react').ReactNode} children
 */

/**
 * Desktop sidebar group order + labels. Settings sits outside (pinned bottom).
 * @type {NavGroupDef[]}
 */
export const NAV_GROUPS = [
  { key: 'today',         label: 'Today' },
  { key: 'clients-work',  label: 'Clients & Work' },
  { key: 'money',         label: 'Money' },
  { key: 'crew',          label: 'Crew' },
]

// ─── Role Guard ────────────────────────────────────────────
/**
 * @param {RoleGuardProps} props
 */
export function RoleGuard({ allowed, pageKey, children }) {
  const { profile, canAccessPage } = useAuth()
  if (!profile?.role) return <Navigate to="/dashboard" replace />
  if (pageKey && !canAccessPage(pageKey)) return <Navigate to="/dashboard" replace />
  if (!pageKey && !allowed?.includes(/** @type {UserRole} */ (profile.role))) return <Navigate to="/dashboard" replace />
  return children
}

// ─── All nav links definition ──────────────────────────────
// Ordered by NAV_GROUPS above — within each group, by importance/frequency.
/** @type {NavLink[]} */
export const ALL_LINKS = [
  // Today — daily ops surfaces
  { to: '/dashboard',   icon: 'nav-dashboard',    label: 'Dashboard',   labelKey: 'dashboard',       roles: ['owner', 'operator'], mobileNav: true,  pageKey: 'dashboard',   group: 'today' },
  { to: '/schedule',    icon: 'nav-schedule',     label: 'Schedule',    labelKey: 'schedule',        roles: ['owner', 'operator'], mobileNav: true,  pageKey: 'schedule',    group: 'today' },
  { to: '/field-work',  icon: 'nav-field-work',   label: 'Field Work',  labelKey: 'field_work',      roles: ['owner', 'operator'], mobileNav: true,  pageKey: 'field-work',  group: 'today' },
  { to: '/approvals',   icon: 'nav-approvals',    label: 'Approvals',   roles: ['owner', 'operator'], mobileNav: false, gate: 'approvals',   pageKey: 'approvals',   group: 'today' },
  // Clients & Work — customer-facing lifecycle
  { to: '/clients',     icon: 'nav-clients',      label: 'Clients',     labelKey: 'client_plural',   roles: ['owner', 'operator'], mobileNav: false, pageKey: 'clients',     group: 'clients-work' },
  { to: '/quotes',      icon: 'nav-quotes',       label: 'Quotes',      labelKey: 'quote_plural',    roles: ['owner', 'operator'], mobileNav: false, gate: 'invoices',    pageKey: 'quotes',      group: 'clients-work' },
  { to: '/leads',       icon: 'nav-leads',        label: 'Leads',       roles: ['owner', 'operator'], mobileNav: false, gate: 'leads',       pageKey: 'leads',       group: 'clients-work' },
  { to: '/contracts',   icon: 'nav-contracts',    label: 'Contracts',   labelKey: 'contract_plural', roles: ['owner', 'operator'], mobileNav: false, gate: 'contracts',   pageKey: 'contracts',   group: 'clients-work' },
  // Money — revenue + cost
  { to: '/invoices',    icon: 'nav-invoices',     label: 'Invoices',    labelKey: 'invoice_plural',  roles: ['owner', 'operator'], mobileNav: false, gate: 'invoices',    pageKey: 'invoices',    group: 'money' },
  { to: '/expenses',    icon: 'nav-expenses',     label: 'Expenses',    roles: ['owner', 'operator'], mobileNav: true,  gate: 'expenses',    pageKey: 'expenses',    group: 'money' },
  { to: '/financials',  icon: 'nav-financials',   label: 'Financials',  roles: ['owner', 'operator'], mobileNav: false, gate: 'financials',  pageKey: 'financials',  group: 'money' },
  // Crew — team + evidence
  { to: '/crew',        icon: 'nav-crew',         label: 'Crew',        labelKey: 'crew',            roles: ['owner', 'operator'], mobileNav: true,  pageKey: 'crew',        group: 'crew' },
  { to: '/compliance',  icon: 'nav-compliance',   label: 'Compliance',  roles: ['owner', 'operator'], mobileNav: false, pageKey: 'compliance',  group: 'crew' },
  { to: '/utilization', icon: 'nav-utilization',  label: 'Utilization', roles: ['owner', 'operator'], mobileNav: false, gate: 'utilization', pageKey: 'utilization', group: 'crew' },
  { to: '/forms',       icon: 'nav-forms',        label: 'Forms',       labelKey: 'form_plural',     roles: ['owner', 'operator'], mobileNav: false, gate: 'forms',       pageKey: 'forms',       group: 'crew' },
  { to: '/documents',   icon: 'nav-documents',    label: 'Documents',   roles: ['owner', 'operator'], mobileNav: false, gate: 'documents',   pageKey: 'documents',   group: 'crew' },
  // Settings — pinned bottom (rendered outside groups)
  { to: '/settings',    icon: 'nav-settings',     label: 'Settings',    roles: ['owner'],             mobileNav: false, pageKey: 'settings',    group: 'settings' },
]
